import type { PresentationItemPlayData } from '@carboncopyco/types';
import type { IGlobalTab } from '@carboncopyco/types/desktop';
import { useAnalytics } from '@carboncopyco/utils';
/* eslint-disable @typescript-eslint/no-empty-function */
import { useRef } from 'react';
import { AnalyticsEvents } from '../../shared/constants/analytics.events';
import { useFlagsFallback } from './useFlagsFallback';

const TIME_BETWEEN_FIRST_TAB_LOAD = 10000;
const TIME_BETWEEN_SEQUENTIAL_TAB_LOAD = 5000;

export const useDelayLoadingPlaybookTab = (tab: IGlobalTab) => {
  const { playbookLoadTabsSequentially, playbookTabLoadTimeout } =
    useFlagsFallback();
  const { trackEvent } = useAnalytics();

  const loadingTime = useRef(0);
  const overrideLoadingTab = useRef(false);
  const presentations = tab.payload.play?.presentations as
    | PresentationItemPlayData[]
    | undefined;

  // if tab is not playbook do nothing since we use this hook for demo preview too
  if (!presentations) {
    return {
      delayLoadingPlaybookTab: (onLoad: () => void) => onLoad(),
      setTabLoadingOverriden: () => {},
      logPlaybookTabLoad: () => {},
    };
  }

  const playbookTabCount = presentations.length;
  const tabIndex = presentations.findIndex(
    (item) => String(item.presentationId) === tab.presentationId
  );

  const delayLoadingPlaybookTab = (onLoad: () => void) => {
    let loadTime = 0;
    if (tabIndex > 0) {
      loadTime = playbookLoadTabsSequentially
        ? TIME_BETWEEN_FIRST_TAB_LOAD +
          TIME_BETWEEN_SEQUENTIAL_TAB_LOAD * tabIndex
        : playbookTabLoadTimeout;
    }
    console.log(
      `load time for tab ${tabIndex + 1} out of ${playbookTabCount} is ${loadTime}`
    );
    loadingTime.current = Date.now();

    setTimeout(onLoad, loadTime);
  };

  const setTabLoadingOverriden = () => (overrideLoadingTab.current = true);

  const shouldLoadInBackground = tabIndex > 0 && !overrideLoadingTab.current;

  const logPlaybookTabLoad = () => {
    console.log(
      `${
        tabIndex > 0 && overrideLoadingTab.current ? 'force ' : ''
      }loading tab ${tabIndex + 1} after ${Date.now() - loadingTime.current}ms`
    );
    trackEvent(AnalyticsEvents.PRESENTATION_TAB_LOAD, {
      variables: {
        tabNumber: tabIndex + 1,
        loadTime: Date.now() - loadingTime.current,
        playbookId: String(tab.payload.play.playbookId),
        presentationId: String(tab.presentationId),
        isForced: tabIndex > 0 && overrideLoadingTab.current,
        sequentialLoadingEnabled: playbookLoadTabsSequentially,
      },
    });
  };

  return {
    delayLoadingPlaybookTab,
    setTabLoadingOverriden,
    logPlaybookTabLoad,
    shouldLoadInBackground,
  };
};
